import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface Eligibility {
  eligibilityId: string;
  invoiceNumber: string;
  invoiceDate: string;
  netPayableAmount: number;
  paymentStatusCode: string;
}

/**
 * The stages a bill really passes through on its way to payment, in order -
 * the statuses the payment services set (voucher, its approval, the payment
 * entered, its reconciliation, a reversal, a hold, closure). Statuses of the
 * retired bank-file flow are never set and are not offered.
 */
export const PAYMENT_STAGES = [
  'PAYMENT_ELIGIBLE',
  'PAYMENT_PROPOSAL_CREATED',
  'PAYMENT_APPROVED',
  'PAYMENT_CONFIRMED',
  'SETTLEMENT_COMPLETED',
  'PAYMENT_REVERSED',
  'ON_HOLD',
  'PAYMENT_CLOSED',
] as const;

interface Props {
  readonly value: string;
  /** The bill chosen and the stage it is at; '' when the choice is cleared. */
  readonly onPick: (eligibilityId: string, stage: string) => void;
}

/**
 * Picks a bill from those in the payment process instead of asking for an id.
 * One list, grouped by the stage each bill is at and counted, so a person sees
 * at a glance where every bill stands and never meets an empty stage.
 */
export function EligibilityPicker({ value, onPick }: Props): ReactNode {
  const { day } = useBusinessDay();
  const { t } = useTranslation();
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [byStage, setByStage] = useState<Array<[string, Eligibility[]]>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    // The list endpoint takes one status, so every stage is asked for at once.
    Promise.all(
      PAYMENT_STAGES.map((s) =>
        apiRequest<Eligibility[]>(`/v1/payments/eligibility/status/${s}`, { token })
          .then((r): [string, Eligibility[]] => [s, Array.isArray(r) ? r : []]),
      ),
    )
      .then((rows) => { if (alive) setByStage(rows.filter(([, items]) => items.length > 0)); })
      .catch((e) => { if (alive) { setByStage([]); setError((e as Error).message); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const stageOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const [stage, items] of byStage) for (const e of items) m.set(e.eligibilityId, stage);
    return m;
  }, [byStage]);

  const label = (e: Eligibility) =>
    [e.invoiceNumber, day(e.invoiceDate), `₹${money(e.netPayableAmount)}`].join(' · ');

  return (
    <div className="tl-stack tl-stack--col" style={{ gap: 6, marginBottom: 20, maxWidth: 560 }}>
      <label htmlFor="elig-select" style={{ fontWeight: 600, fontSize: 14 }}>{t('finance.picker.bill')}</label>
      <select
        id="elig-select"
        data-testid="elig-select"
        value={value}
        disabled={loading}
        style={{ height: 42, padding: '0 12px', borderRadius: 8, border: '1px solid var(--color-border-default)', background: 'var(--color-bg-surface)', fontSize: 14 }}
        onChange={(e) => onPick(e.target.value, stageOf.get(e.target.value) ?? '')}
      >
        <option value="">
          {loading
            ? t('finance.picker.loading')
            : byStage.length === 0
              ? t('finance.picker.none')
              : t('finance.picker.choose')}
        </option>
        {byStage.map(([stage, items]) => (
          <optgroup
            key={stage}
            data-testid={`elig-group-${stage}`}
            label={t('finance.picker.group', { stage: t(`finance.stage.${stage}`), count: items.length })}
          >
            {items.map((e) => (
              <option key={e.eligibilityId} value={e.eligibilityId}>{label(e)}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {error ? <span role="alert" style={{ color: 'var(--color-danger)' }}>{error}</span> : null}
    </div>
  );
}
